/**
 *
 * PaymentPromoPy
 *
 */

import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import NewText from 'Components/NewText';
import Button from 'Components/Button';
import Icon from 'Components/Icon';
import Portal from 'Components/Portal';
import Heading from 'Components/Heading';
import itauLogo from 'Assets/images/itau_logo.png';

/**
 * Configuración de la campaña promocional
 * Define todas las propiedades de la campaña activa
 */
interface CampaignConfig {
  active: boolean;
  name: string;
  startDate: {
    year: number;
    month: number; // 1-12 (enero = 1, diciembre = 12)
    day: number;
    hour: number; // 0-23
    minute: number; // 0-59
  };
  endDate: {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
  };
  installments: number; // Número de cuotas durante la campaña
  pointsMultiplier: number | null; // Multiplicador de puntos (ej: 2 para "puntos X 2"), null si no aplica
  pointsActionType: 'accumulate' | 'worth' | null; // Tipo de acción: 'accumulate' = acumulas X N, 'worth' = tus puntos valen X N, null si no aplica
  specialConditions: string | null; // Condiciones especiales de la campaña (ej: "puntos X 2 únicamente válido en puntos de venta presenciales de Mevuelo.com"), null si no aplica
  utcOffset: number; // Offset UTC en horas (ej: -3 para GMT-3, Paraguay)
}

/**
 * Configuración de la campaña actual
 * Modificar estos valores para activar/desactivar o cambiar la campaña
 */
const CAMPAIGN_CONFIG: CampaignConfig = {
  active: true,
  name: 'Pre-Black Friday Itaú',
  startDate: {
    year: 2025,
    month: 11, // Noviembre
    day: 19,
    hour: 0,
    minute: 0,
  },
  endDate: {
    year: 2025,
    month: 11, // Noviembre
    day: 21,
    hour: 23,
    minute: 59,
  },
  installments: 15,
  pointsMultiplier: 2, // null si no aplica puntos
  pointsActionType: 'worth', // 'accumulate' = acumulas X N, 'worth' = tus puntos valen X N
  specialConditions: "Tus puntos valen X 2 únicamente válido en puntos de venta presenciales de Mevuelo.com", // null o condiciones especiales específicas de la campaña
  utcOffset: -3, // GMT-3 (Paraguay)
};

/**
 * Configuración por defecto (cuando no hay campaña activa)
 */
const DEFAULT_CONFIG = {
  installments: 12,
  pointsMultiplier: null,
};

/**
 * Convierte una fecha UTC a la zona horaria especificada por el offset
 * @param date Fecha en UTC
 * @param utcOffset Offset en horas (ej: -3 para GMT-3)
 * @returns Objeto con año, mes, día, hora y minuto en la zona horaria especificada
 */
const getLocalDateTime = (date: Date, utcOffset: number) => {
  // Obtener UTC en milisegundos
  const utcTime = date.getTime() + (date.getTimezoneOffset() * 60 * 1000);
  // Aplicar el offset (convertir horas a milisegundos)
  const localTime = utcTime + (utcOffset * 60 * 60 * 1000);
  const localDate = new Date(localTime);
  
  return {
    year: localDate.getUTCFullYear(),
    month: localDate.getUTCMonth() + 1, // getUTCMonth() retorna 0-11, necesitamos 1-12
    day: localDate.getUTCDate(),
    hour: localDate.getUTCHours(),
    minute: localDate.getUTCMinutes(),
  };
};

/**
 * Genera el texto para mostrar los puntos según el tipo de acción
 */
const getPointsText = (multiplier: number, actionType: 'accumulate' | 'worth' | null): string => {
  if (actionType === 'worth') {
    return `tus puntos valen X ${multiplier}`;
  } else if (actionType === 'accumulate') {
    return `acumulas X ${multiplier} puntos`;
  }
  return `puntos X ${multiplier}`;
};

/**
 * Genera el texto descriptivo para las condiciones según el tipo de acción
 */
const getPointsDescription = (multiplier: number, actionType: 'accumulate' | 'worth' | null): string => {
  if (actionType === 'worth') {
    const multiplierText = multiplier === 2 ? 'el doble' : `${multiplier} veces más`;
    return `Tus puntos valen ${multiplierText}: Tus puntos acumulados tienen ${multiplierText} de valor al momento de canjearlos con tarjetas de crédito Itaú durante el período promocional.`;
  } else if (actionType === 'accumulate') {
    const multiplierText = multiplier === 2 ? 'el doble' : `${multiplier} veces más`;
    return `Acumula ${multiplierText} de puntos: Acumula ${multiplierText} de puntos en todas tus compras con tarjetas de crédito Itaú durante el período promocional.`;
  }
  // Fallback para compatibilidad
  const multiplierText = multiplier === 2 ? 'el doble' : `${multiplier} veces más`;
  return `Puntos X ${multiplier}: Acumula ${multiplierText} de puntos en todas tus compras con tarjetas de crédito Itaú durante el período promocional.`;
};

/**
 * Verifica si estamos en el rango de fechas de la campaña activa
 */
const isCampaignActive = (config: CampaignConfig): boolean => {
  if (!config.active) {
    return false;
  }

  const now = new Date();
  
  // Obtener fecha/hora actual en la zona horaria especificada por el offset
  const localDateTime = getLocalDateTime(now, config.utcOffset);
  
  const { startDate, endDate } = config;
  
  // Crear timestamps para comparación
  const currentTimestamp = localDateTime.year * 10000000000 + localDateTime.month * 100000000 + localDateTime.day * 1000000 + localDateTime.hour * 10000 + localDateTime.minute * 100;
  const startTimestamp = startDate.year * 10000000000 + startDate.month * 100000000 + startDate.day * 1000000 + startDate.hour * 10000 + startDate.minute * 100;
  const endTimestamp = endDate.year * 10000000000 + endDate.month * 100000000 + endDate.day * 1000000 + endDate.hour * 10000 + endDate.minute * 100;
  
  return currentTimestamp >= startTimestamp && currentTimestamp <= endTimestamp;
};

export const PaymentPromoPy = () => {
  const [showModal, setShowModal] = useState(false);
  const [isActiveCampaign, setIsActiveCampaign] = useState(false);

  useEffect(() => {
    setIsActiveCampaign(isCampaignActive(CAMPAIGN_CONFIG));
  }, []);

  useEffect(() => {
    document.body.classList.toggle('non-scroll', showModal);
    return () => {
      document.body.classList.remove('non-scroll');
    };
  }, [showModal]);

  // Determinar configuración a usar (campaña activa o default)
  const currentConfig = isActiveCampaign ? CAMPAIGN_CONFIG : null;
  const installments = currentConfig?.installments ?? DEFAULT_CONFIG.installments;
  const pointsMultiplier = currentConfig?.pointsMultiplier ?? DEFAULT_CONFIG.pointsMultiplier;
  const pointsActionType = currentConfig?.pointsActionType ?? null;

  return (
    //PARAGUAY
    <PromoBanner>
      <Container onClick={() => setShowModal(true)}>
        <Logo alt="me-vuelo" className="logo" src={itauLogo} />
        <ContentWrapper>
          <Feecontainer>
            <NewText> hasta </NewText>
            <NewText
              className="fees"
              fontSize="36px"
              fontWeight="bold"
              color="orange">
              {' '}
              {installments}{' '}
            </NewText>
            <NewText> cuotas sin intereses </NewText>
          </Feecontainer>
        </ContentWrapper>
        <TextContainer>
          {pointsMultiplier && (
            <PointsContainer>
              <PointsText
                fontSize="24px"
                fontWeight="bold"
                color="orange">
                {getPointsText(pointsMultiplier, pointsActionType)}
              </PointsText>
            </PointsContainer>
          )}
          <NewText color="orange">
            con tus tarjetas de crédito <b>Itaú</b>
          </NewText>
        </TextContainer>
        <Button className="ver-mas">
          <ButtonLabel>Ver más</ButtonLabel>
        </Button>
      </Container>
      {showModal ? (
        <TermsAndConditions 
          onClose={() => setShowModal(false)} 
          campaignConfig={currentConfig}
        />
      ) : null}
    </PromoBanner>
  );
};
const PromoBanner = styled.div`
  ${({ theme }) => css`
    margin-bottom: ${theme.spacing.three};
    margin-top: ${theme.spacing.three};
  `}
`;

const ButtonLabel = styled.span`
  white-space: nowrap;
`;

const Logo = styled.img`
  ${({ theme }) => css`
    max-height: 60px;
    margin-right: ${theme.spacing.two};
  `}
`;

const Container = styled.div`
  ${({ theme }) => css`
    margin-bottom: ${theme.spacing.three};
    margin-top: ${theme.spacing.three};
    background-color: ${theme.palette.grays.white};
    border-radius: ${theme.borderRadius.default};
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: ${theme.spacing.four};
    max-width: 1100px;
    margin: auto;
    gap: ${theme.spacing.two};
    .fees {
      padding-top: ${theme.spacing.one};
      padding-bottom: ${theme.spacing.one};
    }
    @media screen and (max-width: 767px) {
      position: relative;
      max-width: 95%;
      margin-bottom: ${theme.spacing.six};
      margin-top: ${theme.spacing.six};
      padding: ${theme.spacing.three};
      flex-wrap: wrap;
      justify-content: center;
      .ver-mas {
        display: none;
      }
    }
  `}
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.one};
  flex: 1;
`;

const Feecontainer = styled.div`
  display: inline-grid;
  text-align: center;
`;

const PointsContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing.one};
`;

const PointsText = styled(NewText)`
  ${({ theme }) => css`
    @media screen and (max-width: 767px) {
      font-size: 16px !important;
      line-height: 1.3;
      word-break: break-word;
      overflow-wrap: break-word;
    }
  `}
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  text-align: center;
  ${({ theme }) => css`
    @media screen and (max-width: 767px) {
      max-width: 100%;
      min-width: 0;
    }
  `}
`;

interface TermsAndConditionsProps {
  onClose: () => void;
  campaignConfig: CampaignConfig | null;
}

const paragraphProps = {
  paragraph: true,
  type: 'small' as const
};

/**
 * Formatea una fecha para mostrar en el modal
 */
const formatDate = (date: CampaignConfig['startDate']): string => {
  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  return `${date.day} de ${months[date.month - 1]} de ${date.year}`;
};

const TermsAndConditions = ({ onClose, campaignConfig }: TermsAndConditionsProps) => {
  const isCampaignMode = campaignConfig !== null;
  
  return (
    <Portal>
      <ModalOverlay>
        <Modal>
          <ModalCard>
            <Close onClick={onClose}>
              <Icon name="mv-cross" size={20} color="default" />
            </Close>
            <Heading type="h3">
              {isCampaignMode ? (
                <>
                  Condiciones de la promo:
                  <br />
                  {campaignConfig.name}
                </>
              ) : (
                'Condiciones de la promo Itaú'
              )}
            </Heading>
            {isCampaignMode ? (
              <>
                <NewText {...paragraphProps}>
                  Promoción válida del {formatDate(campaignConfig.startDate)} al {formatDate(campaignConfig.endDate)} para todos los clientes con tarjetas de crédito emitidas por el
                  BANCO ITAÚ PARAGUAY S.A.
                </NewText>
                <NewText {...paragraphProps}>
                  Hasta {campaignConfig.installments} cuotas sin intereses con cualquier tarjeta de crédito Itaú durante la campaña {campaignConfig.name}.
                </NewText>
                {campaignConfig.pointsMultiplier && (
                  <NewText {...paragraphProps}>
                    {getPointsDescription(campaignConfig.pointsMultiplier, campaignConfig.pointsActionType)}
                  </NewText>
                )}
                {campaignConfig.specialConditions && (
                  <NewText {...paragraphProps}>
                    {campaignConfig.specialConditions}
                  </NewText>
                )}
                <NewText {...paragraphProps}>
                  El fraccionamiento se realizará bajo solicitud del cliente para
                  todos los productos comercializados por Mevuelo Paraguay (REAL CONSULTING S.A.) a través de
                  la plataforma digital, canales telefónicos o presenciales.
                </NewText>
              </>
            ) : (
              <>
                <NewText {...paragraphProps}>
                  Promoción válida hasta al 31 de Diciembre de
                  2026 para todos los clientes con tarjetas de crédito emitidas por el
                  BANCO ITAÚ PARAGUAY S.A.
                </NewText>
                <NewText {...paragraphProps}>
                  Hasta 10 cuotas sin intereses con cualquier tarjeta de crédito Itaú
                </NewText>
                <NewText {...paragraphProps}>
                  Hasta {DEFAULT_CONFIG.installments} cuotas sin intereses con las Tarjetas AMEX emitidas por banco Itaú Paraguay para Personal Bank.
                </NewText>
                <NewText {...paragraphProps}>
                  El fraccionamiento se realizará bajo solicitud del cliente para
                  todos los productos comercializados por Mevuelo Paraguay (REAL CONSULTING S.A.) a través de
                  la plataforma digital, canales telefónicos o presenciales.
                </NewText>
              </>
            )}
          </ModalCard>
        </Modal>
      </ModalOverlay>
    </Portal>
  );
};

const ModalOverlay = styled.div`
  min-width: 100vw;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  overflow: auto;
  background-color: rgba(0, 0, 0, 0.25);
  ${({ theme }) => css`
    z-index: ${theme.zIndex.modal};
  `}
`;

const Modal = styled.div`
  max-width: 800px;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
`;

const ModalCard = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.white};
    padding: ${theme.spacing.four};
    display: flex;
    flex-direction: column;
    position: relative;
    h4 {
      margin-bottom: ${theme.spacing.two};
    }
    @media screen and (min-width: 767px) {
      margin: ${theme.spacing.four};
      border-radius: ${theme.borderRadius.default};
      min-height: 50vh;
    }
    @media screen and (max-width: 767px) {
      min-height: 100vh;
      padding-top: ${theme.spacing.twelve};
    }
  `}
`;

const Close = styled.div`
  ${({ theme }) => css`
    position: absolute;
    top: ${theme.spacing.two};
    right: ${theme.spacing.two};
    cursor: pointer;
  `}
`;
